import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AuctionSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentInfo, setPaymentInfo] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const { data } = await axios.get(
        `${API}/api/checkout/status/${sessionId}`,
        { withCredentials: true }
      );

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentInfo(data);
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen pt-20 flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        {status === 'loading' && (
          <div className="reveal-item">
            <Loader2 size={64} className="mx-auto text-[#C5A059] mb-8 animate-spin" />
            <h1 className="font-serif text-3xl text-[#F5F5F0] mb-4">
              Processing Payment
            </h1>
            <p className="text-[#A3A3A3]">
              Please wait while we confirm your bid...
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="reveal-item">
            <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-[#C5A059]/20 flex items-center justify-center">
              <CheckCircle size={48} className="text-[#C5A059]" />
            </div>
            <h1 className="font-serif text-4xl text-[#F5F5F0] mb-4">
              Bid Placed Successfully!
            </h1>
            <p className="text-[#A3A3A3] mb-8">
              Your bid has been recorded. You will be notified if you win the auction.
              Good luck!
            </p>
            
            <div className="p-6 bg-[#121212] border border-white/10 text-left mb-8">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-4">
                Payment Details
              </p>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-[#A3A3A3] text-sm">Auction Fee</span>
                  <span className="text-[#F5F5F0]">$20.00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#A3A3A3] text-sm">Status</span>
                  <span className="text-[#C5A059]">Confirmed</span>
                </div>
              </div>
            </div>

            <Link
              to="/auction"
              className="inline-flex items-center gap-3 bg-[#C5A059] text-[#0A0A0A] px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#E0BE77] transition-colors"
              data-testid="back-to-auction"
            >
              Back to Auction
              <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="reveal-item">
            <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-[#D84B79]/20 flex items-center justify-center">
              <XCircle size={48} className="text-[#D84B79]" />
            </div>
            <h1 className="font-serif text-4xl text-[#F5F5F0] mb-4">
              Payment Failed
            </h1>
            <p className="text-[#A3A3A3] mb-8">
              We couldn't process your payment. Please try again or contact support
              if the issue persists.
            </p>
            
            <Link
              to="/auction"
              className="inline-flex items-center gap-3 border border-white/20 text-[#F5F5F0] px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
              data-testid="try-again"
            >
              Try Again
              <ArrowRight size={16} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
